import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLogin } from '../../../helpers/api/auth/auth';
import { apiClient } from '../../../helpers/apiHelper';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';

// Import icons
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { RiShieldLine } from 'react-icons/ri';
import { FormField } from '../../ComponnentsUI/common/FormField';
import { Input } from '../../ComponnentsUI/common/Input';
import { Button } from '../../ComponnentsUI/common/Button';
import { IoFlash } from 'react-icons/io5';
import smallLogo from '../../../small_deepecho_icon.png';
import darksmallLogo from '../../../small_deepecho_icon_dark.png';
const Login: React.FC = () => {
  const { t } = useTranslation('Login');
  
  // Explicit state management for the form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // All your original logic state is preserved
  const [error, setError] = useState('');
  const [lockoutSecondsRemaining, setLockoutSecondsRemaining] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  // TOTP state
  const [showTOTP, setShowTOTP] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);
  const navigate = useNavigate();
 const [searchParams] = useSearchParams(); 
  const { mutateAsync: login, isPending } = useLogin();
  const backend_url =
    (window as any).RUNTIME_ENV?.BACKEND_URL ??
    process.env.BACKEND_URL ??
    'http://localhost:8000/api/v1';

  // This useEffect logic is identical to your original code
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const response = await apiClient.get('/settings/features-enabled');
        setSsoEnabled(response.data.sso_enabled || false);
      } catch (error) {
        console.error('Error fetching features:', error);
      }
    };
    fetchFeatures();
  }, [backend_url]);

  useEffect(() => {
    if (lockoutSecondsRemaining <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setLockoutSecondsRemaining((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [lockoutSecondsRemaining]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Necessary for standard <form> onSubmit
    setError('');
    setLockoutSecondsRemaining(0);
    setIsLoading(true);
    setShowTOTP(false);
    setTotpCode('');
    setPendingCredentials(null);
    try {
      const response = await login({
        email: email,
        password: password,
      });
      if ((response as any)?.twofa_required) {
        setShowTOTP(true);
        setPendingCredentials({ email: email, password: password });
        setIsLoading(false);
        return;
      }
      if ((response as any)?.force_password_change_required) {
        setIsLoading(false);
        navigate('/force-password-change', {
          state: {
            verificationCodeRequired:
              (response as any)?.verification_code_required !== false,
          },
        });
        return;
      }
      if ((response as any)?.force_2fa_setup_required) {
        setIsLoading(false);
        navigate('/2fa-setup');
        return;
      }
      setIsLoading(false);
      navigate('/home');
    } catch (err) {
      setIsLoading(false);
      setError(getLoginErrorMessage(err, t('errors.invalidCredentials')));
    }
  };

  const handleTOTPSubmit = async () => {
    if (!pendingCredentials) return;
    setError('');
    setIsLoading(true);
    try {
      const response = await login({
        ...pendingCredentials,
        totp_code: totpCode,
      });
      if ((response as any)?.force_2fa_setup_required) {
        setIsLoading(false);
        navigate('/2fa-setup');
        return;
      }
      if ((response as any)?.force_password_change_required) {
        setIsLoading(false);
        navigate('/force-password-change', {
          state: {
            verificationCodeRequired:
              (response as any)?.verification_code_required !== false,
          },
        });
        return;
      }
      setIsLoading(false);
      navigate('/home');
    } catch (err) {
      setIsLoading(false);
      setError(getLoginErrorMessage(err, t('errors.invalidTOTP')));
      console.error('TOTP Login error:', err);
    }
  };

  const handleSSO = async () => {
    window.location.href = backend_url + '/saml/login';
  };

  const handleGoogleOAuth = () => {
    window.location.href = backend_url + '/auth/google/login';
  };

  const getLoginErrorMessage = (err: any, fallback: string) => {
    const statusCode = err?.response?.status ?? err?.status_code;
    const backendMessage = err?.response?.data?.message ?? err?.message;
    const retryAfterSeconds =
      err?.response?.data?.details?.retry_after_seconds ??
      err?.details?.retry_after_seconds;

    if (typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0) {
      setLockoutSecondsRemaining(retryAfterSeconds);
    }

    if (statusCode === 429 && backendMessage) {
      return backendMessage;
    }

    return fallback;
  };

  const formatLockoutTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes <= 0) {
      return `${remainingSeconds}s`;
    }

    return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`;
  };
  useEffect(() => {
    console.log("Checking for OAuth errors in URL...",searchParams);
      const errorType = searchParams.get('error');
      if (errorType) {
        if (errorType === 'user_not_found') {
          setError("This account does not have access. Please contact your administrator.");
        } else if (errorType === 'inactive_account') {
          setError("Your account is currently inactive.");
        } else {
          setError("Google authentication failed. Please try again.");
        }
        
        // Optional: Clean up the URL after reading the error
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }, [searchParams]);
  useDocumentTitle(`${t('title')} - ${t('signIn')}`);

  return (
    <div className="min-h-screen w-full flex justify-center items-center p-4 bg-card-color-200 dark:bg-gray-900">
      <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-lg bg-card-color-100 dark:bg-gray-800">
        {/* Visual elements are the same */}
        <div className="flex items-center justify-center mb-6 space-x-3">
          <img
            src={smallLogo}
            className={`dark:hidden transition-all duration-300 h-10`}
            alt="Deep Echo Logo"
          />
          <img
            src={darksmallLogo}
            className="hidden dark:block transition-all duration-300 h-10"
            alt="Deep Echo Logo"
          />
          <div>
            <h1 className="text-xl font-bold text-title-page-color-100">
              {t('title')}
            </h1>
            <p className="text-xs text-gray-500">{t('subtitle')}</p>
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-title-page-color-100">
            {t('welcomeTitle')}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t('welcomeSubtitle')}
          </p>
        </div>
        {!showTOTP ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label={t('emailLabel')} htmlFor="email">
              <Input
                id="email"
                type="email"
                placeholder={t('emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-gray-50 border-gray-200 focus:bg-white"
              />
            </FormField>

            <FormField label={t('passwordLabel')} htmlFor="password">
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-gray-50 border-gray-200 focus:bg-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </FormField>

            <div className="flex items-center justify-end text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                {t('forgotPassword')}
              </Link>
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center font-medium">
                {error}
                {lockoutSecondsRemaining > 0
                  ? ` (${formatLockoutTime(lockoutSecondsRemaining)})`
                  : ''}
              </p>
            )}

            <div className="pt-2 space-y-4">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isPending || lockoutSecondsRemaining > 0}
              >
                {isPending ? t('signingIn') : t('signIn')}
              </Button>

              {/* Google OAuth button - always available */}
              <Button
                type="button"
                variant="secondary"
                size="lg"
                className="w-full"
                disabled={isPending}
                onClick={handleGoogleOAuth}
              >
                Continue with Google
              </Button>

              {/* SSO/Workspace button - only shown when SSO is enabled */}
              {ssoEnabled && (
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className="w-full"
                  disabled={isPending}
                  onClick={handleSSO}
                >
                  Sign in with Workspace SSO
                </Button>
              )}
            </div>
          </form>) : (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                  <RiShieldLine className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-2">
                  {t('twoFactorTitle')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('twoFactorSubtitle')}
                </p>
              </div>

              <FormField label={t('authCodeLabel')} htmlFor="totp">
                <Input
                  id="totp"
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  maxLength={6}
                  minLength={6}
                  placeholder={t('authCodePlaceholder')}
                  autoFocus
                  className="text-center text-lg tracking-wider bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-600"
                />
              </FormField>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400 text-center">
                    {error}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={handleTOTPSubmit}
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isLoading || totpCode.length !== 6}
                >
                  <RiShieldLine className="w-4 h-4 mr-2" />
                  {isLoading ? t('verifying') : t('verifyCode')}
                </Button>

                <Button
                  onClick={() => { setShowTOTP(false); setTotpCode(''); setPendingCredentials(null); setError(''); }}
                  variant="ghost"
                  size="lg"
                  className="w-full"
                  disabled={isLoading}
                >
                  {t('backToLogin')}
                </Button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default Login;
